import { Link } from '@tanstack/react-router'
import { ArrowRight, type LucideIcon } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'

export function cn(...parcalar: Array<string | false | null | undefined>) {
  return parcalar.filter(Boolean).join(' ')
}

export function Container({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('mx-auto w-full max-w-6xl px-5 sm:px-8', className)}>{children}</div>
}

export function Section({
  id,
  className,
  children,
  dar,
}: {
  id?: string
  className?: string
  children: ReactNode
  dar?: boolean
}) {
  return (
    <section id={id} className={cn('py-16 sm:py-24', className)}>
      <Container className={dar ? 'max-w-3xl' : undefined}>{children}</Container>
    </section>
  )
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p
      className={cn(
        'inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.14em] text-brand',
        className,
      )}
    >
      <span className="inline-block size-1.5 rounded-full bg-spark" aria-hidden />
      {children}
    </p>
  )
}

export function Baslik({
  eyebrow,
  baslik,
  metin,
  hizala = 'left',
  seviye = 'h2',
  className,
}: {
  eyebrow?: string
  baslik: ReactNode
  metin?: ReactNode
  hizala?: 'left' | 'center'
  seviye?: 'h1' | 'h2' | 'h3'
  className?: string
}) {
  const H = seviye
  return (
    <div className={cn('max-w-2xl', hizala === 'center' && 'mx-auto text-center', className)}>
      {eyebrow && <Eyebrow className="mb-4">{eyebrow}</Eyebrow>}
      <H
        className={cn(
          'heading text-balance text-ink',
          seviye === 'h1' ? 'display text-4xl sm:text-5xl lg:text-6xl' : '',
          seviye === 'h2' ? 'text-3xl sm:text-4xl' : '',
          seviye === 'h3' ? 'text-2xl' : '',
        )}
      >
        {baslik}
      </H>
      {metin && (
        <p className="mt-4 text-pretty text-base leading-relaxed text-ink-2 sm:text-lg">{metin}</p>
      )}
    </div>
  )
}

type ButonTemel = {
  variant?: 'primary' | 'secondary' | 'ghost' | 'inverse' | 'light'
  size?: 'sm' | 'md' | 'lg'
  ok?: boolean
  ikon?: LucideIcon
  className?: string
  children: ReactNode
}

const BUTON_SINIF = {
  temel:
    'group inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)] font-medium transition-[background-color,color,box-shadow,transform] duration-200 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none',
  variant: {
    primary:
      'bg-brand text-on-brand hover:bg-brand-strong shadow-[0_1px_0_rgba(255,255,255,0.12)_inset,0_8px_20px_-10px_var(--ad-glow)]',
    secondary: 'bg-surface text-ink ring-1 ring-line hover:bg-subtle',
    ghost: 'text-ink-2 hover:text-ink hover:bg-subtle',
    inverse: 'bg-ink text-page hover:opacity-90',
    // Marka yesili zemin ustunde acik buton (CTA bandi).
    light: 'bg-page text-ink hover:bg-page/90',
  },
  size: { sm: 'h-9 px-3 text-sm', md: 'h-11 px-5 text-sm', lg: 'h-12 px-6 text-base' },
}

function butonSinifi(p: ButonTemel) {
  return cn(
    BUTON_SINIF.temel,
    BUTON_SINIF.variant[p.variant ?? 'primary'],
    BUTON_SINIF.size[p.size ?? 'md'],
    p.className,
  )
}

function ButonIcerik({ children, ok, ikon: Ikon }: ButonTemel) {
  return (
    <>
      {Ikon && <Ikon className="size-4" aria-hidden />}
      {children}
      {ok && (
        <ArrowRight
          className="size-4 transition-transform duration-200 group-hover:translate-x-0.5"
          aria-hidden
        />
      )}
    </>
  )
}

/** İç bağlantı. */
export function ButonLink({ to, ...p }: ButonTemel & { to: string }) {
  return (
    <Link to={to} className={butonSinifi(p)}>
      <ButonIcerik {...p} />
    </Link>
  )
}

/** Dış bağlantı (panel, portal, WhatsApp). */
export function ButonHref({ href, ...p }: ButonTemel & { href: string }) {
  const dis = href.startsWith('http')
  return (
    <a href={href} className={butonSinifi(p)} {...(dis ? { rel: 'noreferrer' } : {})}>
      <ButonIcerik {...p} />
    </a>
  )
}

export function Buton({
  variant,
  size,
  ok,
  ikon,
  className,
  children,
  ...rest
}: ButonTemel & Omit<ComponentProps<'button'>, 'className' | 'children'>) {
  return (
    <button className={butonSinifi({ variant, size, ok, ikon, className, children })} {...rest}>
      <ButonIcerik variant={variant} size={size} ok={ok} ikon={ikon}>
        {children}
      </ButonIcerik>
    </button>
  )
}

export function Rozet({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: 'neutral' | 'brand' | 'warning' | 'success' | 'info' | 'danger'
  children: ReactNode
  className?: string
}) {
  const t = {
    neutral: 'bg-subtle text-ink-2 ring-line',
    brand: 'bg-brand-subtle text-brand ring-brand/20',
    warning: 'bg-warning/10 text-warning ring-warning/25',
    success: 'bg-success/10 text-success ring-success/25',
    info: 'bg-info/10 text-info ring-info/25',
    danger: 'bg-danger/10 text-danger ring-danger/25',
  }[tone]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
        t,
        className,
      )}
    >
      {children}
    </span>
  )
}

export function Kart({
  className,
  children,
  hover,
}: {
  className?: string
  children: ReactNode
  hover?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-[var(--radius-panel)] bg-surface ring-soft',
        hover && 'transition-transform duration-300 hover:-translate-y-0.5',
        className,
      )}
    >
      {children}
    </div>
  )
}

/** Sayfa üstü boşluk: sabit menünün altına girmesin. */
export function SayfaUstu({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('pt-28 sm:pt-36', className)}>{children}</div>
}

export const PANEL_URL = 'https://app.akillidernek.com'
export const DEMO_YOLU = '/iletisim'
export const WHATSAPP_URL: string | undefined = import.meta.env.VITE_WHATSAPP_URL
